import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import api from '../lib/api';
import Logo from '../components/Logo';
import AppleLoginButton from '../components/AppleLoginButton';
import { toast } from 'sonner';

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);

  // Surface Apple Sign-In errors that come back on the redirect URL — Apple
  // otherwise leaves the user on a bare "invalid_request" page with zero
  // in-app context.
  React.useEffect(() => {
    const appleError = params.get('apple_error');
    if (appleError) {
      const friendly = appleError === 'user_cancelled_authorize'
        ? 'Apple sign in was cancelled.'
        : `Apple sign in failed (${appleError}). Please try again or use email.`;
      toast.error(friendly);
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) { toast.success('Welcome back \u2728'); api.post('/planets/p5/join').catch(() => {}); nav('/app/planets/p5'); }
    else setError(res.error);
  };

  return (
    <div className="min-h-screen cosmic-bg relative flex items-center justify-center px-4">
      <div className="stars-bg" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <Link to="/" aria-label="Back to WorldVibe home" data-testid="login-home-link" className="hover:opacity-90 transition">
            <Logo size="lg" showText animate />
          </Link>
        </div>
        <div className="glass rounded-3xl p-7 cosmic-border">
          <h1 className="font-display font-bold text-2xl">Welcome back</h1>
          <p className="text-white/60 text-sm mt-1">Sign in to continue your cosmic journey.</p>

          <Button onClick={loginWithGoogle} variant="outline" className="w-full mt-6 bg-white text-black hover:bg-white/90 border-0 font-semibold">
            <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83Z"/><path fill="#EA4335" d="M12 5.38c1.61 0 3.06.55 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </Button>
          <AppleLoginButton label="Continue with Apple" next="/app/planets/p5" />

          <div className="flex items-center gap-3 my-5 text-xs text-white/40">
            <div className="flex-1 h-px bg-white/10" /> or sign in with email <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="you@worldvibe.app" className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={password} onChange={e => setPassword(e.target.value)} type="password" required placeholder="Password" className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
            </div>
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                data-testid="forgot-password-link"
                className="text-xs text-cyan-300 hover:text-cyan-200 transition"
              >
                Forgot password?
              </button>
            </div>
            {error && <div className="text-sm text-rose-400">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white border-0 font-semibold">
              {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <ArrowRight className="mr-2" size={16} />} Sign in
            </Button>
          </form>
          <div className="text-center text-sm text-white/60 mt-5">No account? <Link to="/signup" className="text-cyan-300 hover:text-cyan-200">Create one</Link></div>
        </div>
      </div>
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} defaultEmail={email} />
    </div>
  );
}

function ForgotPasswordDialog({ open, onOpenChange, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) { setEmail(defaultEmail || ''); setSent(false); }
  }, [open, defaultEmail]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      // Backend returns 200 either way to avoid email enumeration
      setSent(true);
    } catch (err) {
      // 422 = bad email shape only — show inline
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Please enter a valid email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Mail size={16} className="text-fuchsia-400" /> Reset your password
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {sent
              ? 'If an account exists for that email, a reset link is on its way.'
              : 'Enter your email and we\u2019ll send you a link to set a new password.'}
          </DialogDescription>
        </DialogHeader>
        {!sent ? (
          <form onSubmit={submit} className="space-y-3" data-testid="forgot-password-form">
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                placeholder="you@worldvibe.app"
                data-testid="fp-email"
                className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || !email.trim()}
                data-testid="fp-submit"
                className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 disabled:opacity-50"
              >
                {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                Send reset link
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="forgot-password-sent">
            <div className="rounded-2xl p-4 bg-emerald-500/10 border border-emerald-400/20 text-sm text-emerald-200">
              Check your inbox for <span className="font-semibold text-white">{email}</span>. The link expires in 1 hour.
            </div>
            <div className="text-[11px] text-white/40">
              Didn&apos;t get it? Check spam, or try again in a few minutes.
            </div>
            <DialogFooter>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0"
              >
                Got it
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
